/**
 * 15 aktivitas PAUD kanonik (plan 21; Doa dihapus 2026-09-09) — persis nama
 * di tabel `activity` DB (migration 0002). Daftar penuh dipakai oleh
 * preferensi premium; guest/non-premium memakai subset Funpaper Harian di
 * bawah. Enam aktivitas lanjutan secara eksplisit hanya tersedia untuk
 * premium.
 *
 * Doa dikeluarkan dari daftar pilihan 2026-09-09: worksheet Doa adalah
 * konten bonus produk terpisah dan sudah di-exclude dari seluruh pool
 * rekomendasi (apps/api/src/lib/catalog-policy.ts) — chip tanpa worksheet
 * hanya akan menyesatkan.
 *
 * Sebelum plan 21 UI memakai 8 display-name pilihan tangan yang memuat
 * `Menghitung Angka` & `Mengenal Huruf` — dua nama yang TIDAK ada di DB
 * sehingga tidak pernah match algo. Daftar ini menggantikannya.
 *
 * Constraint: NO Musik, NO animal-only, NO human-only worksheet
 * activity (hard rule Doc 03).
 *
 * The `name` field is the canonical value that gets written to
 * `user_preferences.preferred_activity` (a JSON array of strings)
 * and is the same string the daily algo matches against
 * worksheet.activity_label.
 *
 * The `popular` flag marks the top-4 defaults used when the user
 * skips the quiz. Urutan array = urutan seed tabel activity DB.
 */

export type Activity = {
  /** Canonical activity name (matches `activity.activity_name` di DB). */
  name: string;
  /** True if this is one of the top-4 popular defaults. */
  popular: boolean;
  /** Lower number = rendered earlier in the list. */
  displayOrder: number;
};

/**
 * Ikon kartu quiz dirender dari activityIcon(name) di lib/icons.ts.
 * Setiap aktivitas kanonik memiliki aset khusus agar kartu premium tidak
 * tampil dengan ikon fallback yang sama.
 */
export const ACTIVITY_LIST: readonly Activity[] = [
  { name: 'Menggunting', popular: true, displayOrder: 1 },
  { name: 'Melingkari Gambar', popular: false, displayOrder: 2 },
  { name: 'Soal Campuran', popular: false, displayOrder: 3 },
  { name: 'Mewarnai', popular: true, displayOrder: 4 },
  { name: 'Menebalkan Garis', popular: true, displayOrder: 5 },
  { name: 'Gunting Tempel', popular: false, displayOrder: 6 },
  { name: 'Maze', popular: true, displayOrder: 7 },
  { name: 'Menghubungkan Titik', popular: false, displayOrder: 8 },
  { name: 'Mencocokkan', popular: false, displayOrder: 9 },
  { name: 'Analisa', popular: false, displayOrder: 10 },
  { name: 'Membandingkan', popular: false, displayOrder: 11 },
  { name: 'Art and Craft', popular: false, displayOrder: 12 },
  { name: 'Menulis dan Menggambar', popular: false, displayOrder: 14 },
  { name: 'Operasi Hitung', popular: false, displayOrder: 15 },
  { name: 'Menulis', popular: false, displayOrder: 16 },
];

/**
 * Activity options reserved for premium users in the onboarding quiz.
 * The free/guest list is the remaining Harian-facing set.
 */
const PREMIUM_ONLY_ACTIVITY_NAMES = new Set([
  'Melingkari Gambar',
  'Soal Campuran',
  'Analisa',
  'Membandingkan',
  'Art and Craft',
  'Operasi Hitung',
]);

/** Activity options exposed to free/guest users from Funpaper Harian. */
const HARIAN_ACTIVITY_NAMES = new Set([
  'Menggunting',
  'Mewarnai',
  'Menebalkan Garis',
  'Gunting Tempel',
  'Maze',
]);

/** Guest/free activity choices; premium can use ACTIVITY_LIST. */
export const HARIAN_ACTIVITY_LIST: readonly Activity[] = ACTIVITY_LIST.filter(
  (activity) =>
    HARIAN_ACTIVITY_NAMES.has(activity.name) &&
    !PREMIUM_ONLY_ACTIVITY_NAMES.has(activity.name),
);

/** Quiz Activity selection bounds (plan 21: min 3, max 5). */
export const ACTIVITY_MIN = 3;
export const ACTIVITY_MAX = 5;

/**
 * Default selection when the user taps "Lewati" — 4 aktivitas populer
 * yang semuanya tersedia di Funpaper Harian.
 */
export const ACTIVITY_DEFAULT_SKIP: readonly string[] = ACTIVITY_LIST.filter(
  (a) => a.popular,
).map((a) => a.name);

export function isActivityValidSelection(
  selected: readonly string[],
): boolean {
  return selected.length >= ACTIVITY_MIN && selected.length <= ACTIVITY_MAX;
}
